import { Controllers } from "@/CommonElements/Controller";
import React from "react";
import { useFormContext } from "react-hook-form";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { visitDateOptions } from "@/Pages/Campaigns/data";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { SelectCountry } from "@/Component/Form/SelectCountry/SelectCountry";
import { LabelValue } from "@/Component/Value";
import { useConfirm } from "@/utils/hooks/useConfrim";
import {
  saudiMobileRules,
  numbersOnlyRules,
} from "@/Pages/ProfileForm/validationUtils";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions/SelectCachedOptions";

const serviceCenterSystemKey = (sc) =>
  String(sc?.system ?? "").toLowerCase();

/** Stable refs so SelectCachedOptions memo + useMemo see consistent filter identity. */
const filterMakkahServiceCenter = (sc) =>
  serviceCenterSystemKey(sc) === "makkah";

const filterMadinaServiceCenter = (sc) => {
  const s = serviceCenterSystemKey(sc);
  return s === "madina";
};

export const InitialCampaignForm = ({
  isLoading = false,
  isViewMode = false,
}) => {
  // ==================
  // Hooks
  // ==================
  const { confirm, ModalComponent } = useConfirm();
  const { cachedBasicData, basicDataResponse } = useCachedBasicData();
  const loading = basicDataResponse?.isLoading || basicDataResponse?.isFetching;
  const { t } = useTranslation();
  const form = useFormContext();
  const {
    control,
    formState: { errors },
  } = form;

  // ==================
  // Constants
  // ==================
  // controllerProps, labelProps, layout
  const controllers = [
    {
      labelProps: {
        children: t("CAMPAIGNS_FORM_CARAVAN_NO_LABEL"),
      },
      controllerProps: {
        name: "campaign_number",
        rules: {
          required: t("FIELD_REQUIRED"),
          ...numbersOnlyRules(t),
          minLength: {
            value: 5,
            message: t("CAMPAIGNS_FORM_CARAVAN_NO_LENGTH"),
          },
          maxLength: {
            value: 5,
            message: t("CAMPAIGNS_FORM_CARAVAN_NO_MAX_LENGTH"),
          },
        },
        render: ({ field }) => (
          <AntInput
            maxLength={5}
            {...field}
            status={errors.campaign_number ? "error" : undefined}
            onChange={(e) => {
              const value = e.target.value;
              field.onChange(value);
              const stateId = value.slice(0, 2);
              const valueTrimmed = value.trim();

              if (valueTrimmed.length >= 2) {
                form.setValue("state_id", Number(stateId), {
                  shouldValidate: true,
                });
              } else {
                form.setValue("state_id", null, {
                  shouldValidate: true,
                });
              }
            }}
            placeholder={t("CAMPAIGNS_FORM_CARAVAN_NO_PLACEHOLDER")}
            type="text"
            id="campaign_number"
            name="campaign_number"
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("CAMPAIGNS_FORM_NAME_LABEL"),
      },
      controllerProps: {
        name: "campaign_name",
        render: ({ field }) => (
          <AntInput
            {...field}
            status={errors.campaign_name ? "error" : undefined}
            onChange={(e) => {
              field.onChange(e.target.value);
              form.setValue("supervisor_name", e.target.value, {
                shouldValidate: true,
              });
            }}
            placeholder={t("CAMPAIGNS_FORM_NAME_PLACEHOLDER")}
            type="text"
            id="campaign_name"
            name="campaign_name"
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("CAMPAIGNS_FORM_MANAGER_LABEL"),
      },
      controllerProps: {
        name: "supervisor_name",
        render: ({ field }) => (
          <AntInput
            {...field}
            status={errors.supervisor_name ? "error" : undefined}
            placeholder={t("CAMPAIGNS_FORM_MANAGER_PLACEHOLDER")}
            type="text"
            id="supervisor_name"
            name="supervisor_name"
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("CAMPAIGNS_FORM_VISIT_LABEL"),
      },
      controllerProps: {
        name: "visit",
        render: ({ field }) => (
          <AntSelect
            status={errors.visit ? "error" : undefined}
            id="visit"
            name="visit"
            placeholder={t("CAMPAIGNS_FORM_VISIT_PLACEHOLDER")}
            {...field}
            options={visitDateOptions(t)}
          />
        ),
      },
      renderValue: () => {
        const value = form.watch("visit");
        return (
          <LabelValue.Value
            loading={isLoading}
            value={t(value?.toUpperCase() ?? "-")}
          />
        );
      },
    },
    {
      labelProps: {
        children: t("CAMPAIGNS_FORM_COUNTRY_LABEL"),
      },

      renderValue: () => {
        const value = form.watch("country_id");
        return <SelectCountry.Display value={value} />;
      },
      controllerProps: {
        name: "country_id",
        isLoading: loading,
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            status={errors.country_id ? "error" : undefined}
            id="country_id"
            name="country_id"
            showSearch
            placeholder={t("CAMPAIGNS_FORM_COUNTRY_PLACEHOLDER")}
            optionKey="nationalities"
            options={(country) => ({
              value: String(country.id),
              label: <SelectCountry.Display value={country.id} />,
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("CAMPAIGNS_FORM_NATIONALITY_LABEL"),
      },
      renderValue: () => {
        const value = form.watch("nationality_id");
        return <SelectCountry.Display value={value} />;
      },
      controllerProps: {
        name: "nationality_id",
        isLoading: loading,
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            status={errors.nationality_id ? "error" : undefined}
            id="nationality_id"
            name="nationality_id"
            showSearch
            placeholder={t("CAMPAIGNS_FORM_NATIONALITY_PLACEHOLDER")}
            optionKey="nationalities"
            options={(nationality) => ({
              value: nationality.id,
              label: <SelectCountry.Display value={nationality.id} />,
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("MAKKAH_SERVICE_CENTER_NUMBER"),
      },
      renderValue: (value) => {
        return (
          <SelectCachedOptions.Display
            optionKey="service_centers"
            filter={filterMakkahServiceCenter}
            value={value}
            campareBy="id"
            render={(option) => (
              <LabelValue.Value
                loading={isLoading}
                value={option?.name || "-"}
              />
            )}
          />
        );
      },
      controllerProps: {
        name: "service_center_id",
        isLoading: loading,
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            status={errors.service_center_id ? "error" : undefined}
            id="service_center_id"
            filter={filterMakkahServiceCenter}
            showSearch
            placeholder={t("CAMPAIGNS_FORM_SERVICE_CENTER_PLACEHOLDER")}
            optionKey="service_centers"
            options={(serviceCenter) => ({
              value: String(serviceCenter.id),
              label: <LabelValue.Value value={serviceCenter.name} />,
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("MADINAH_SERVICE_CENTER_NUMBER"),
      },
      renderValue: (value) => {
        return (
          <SelectCachedOptions.Display
            optionKey="service_centers"
            filter={filterMadinaServiceCenter}
            value={value}
            campareBy="id"
            render={(option) => (
              <LabelValue.Value
                loading={isLoading}
                value={option?.name || "-"}
              />
            )}
          />
        );
      },
      controllerProps: {
        name: "madina_service_center_id",
        isLoading: loading,
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            filter={filterMadinaServiceCenter}
            status={errors.madina_service_center_id ? "error" : undefined}
            id="madina_service_center_id"
            showSearch
            placeholder={t("CAMPAIGNS_FORM_SERVICE_CENTER_PLACEHOLDER")}
            optionKey="service_centers"
            options={(serviceCenter) => ({
              value: String(serviceCenter.id),
              label: <LabelValue.Value value={serviceCenter.name} />,
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("CAMPAIGNS_FORM_PROVINCE_CITY_LABEL"),
      },

      renderValue: (value) => {
        return <SelectCachedOptions.Display campareBy="id" optionKey="states" value={value} render={(option) => <LabelValue.Value value={option?.name || "-"} />} />;
      },
      controllerProps: {
        name: "state_id",
        isLoading: loading,
        rules: {
          validate: (value) => {
            return (
              !!cachedBasicData.states?.find((state) => String(state.id) === String(value)) ||
              t("CAMPAIGNS_FORM_PROVINCE_CITY_VALIDATION")
            );
          },
        },
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            status={errors.state_id ? "error" : undefined}
            onChange={(value) => {
              const stateId = form.getValues("state_id");
              field.onChange(value);

              const campaignNumber = form.getValues("campaign_number");
              const campaignNumberTrimmed = campaignNumber?.trim?.();
              const firstTwoDigits = campaignNumberTrimmed?.slice(0, 2);

              if (
                campaignNumberTrimmed?.length >= 2 &&
                Number(firstTwoDigits) !== value
              ) {
                confirm({
                  title: t("CAMPAIGNS_PROVINCE_CONFIRM_TITLE"),
                  content: t("CAMPAIGNS_PROVINCE_CONFIRM_MESSAGE", {
                    province: value,
                  }),
                  okText: t("CONTINUE"),
                  cancelText: t("CANCEL"),
                  onOk: () => {
                    const newCampaignNumber =
                      value + campaignNumberTrimmed.slice(2);
                    form.setValue("campaign_number", newCampaignNumber, {
                      shouldValidate: true,
                    });
                  },
                  onCancel: () => {
                    form.setValue("state_id", stateId, {
                      shouldValidate: true,
                    });
                  },
                });
              } else {
                form.setValue("campaign_number", String(value), {
                  shouldValidate: true,
                });
              }
            }}
            id="state_id"
            name="state_id"
            showSearch
            placeholder={t("CAMPAIGNS_FORM_PROVINCE_CITY_PLACEHOLDER")}
            optionKey="states"
            options={(state) => ({
              value: String(state.id),
              label: <LabelValue.Value value={state.name} />,
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("MOBILE_NUMBER"),
      },
      controllerProps: {
        name: "supervisor_mobile_number",
        rules: {
          required: t("FIELD_REQUIRED"),
          ...saudiMobileRules(t),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            status={errors.supervisor_mobile_number ? "error" : undefined}
            id="supervisor_mobile_number"
            name="supervisor_mobile_number"
            placeholder={t("05xxxxxxxx")}
          />
        ),
      },
    },
  ];

  // ==================
  // Render
  // ==================
  return (
    <>
      <Controllers
        isLoading={isLoading}
        isViewMode={isViewMode}
        watch={form.watch}
        {...{
          controllers: controllers.map((config) => ({
            ...config,
            labelProps: {
              ...config.labelProps,
              className: cn("fw-bold required", config.labelProps.className),
            },
            controllerProps: {
              ...config.controllerProps,
              rules: {
                ...(config.controllerProps.rules || {}),
                required: t("FIELD_REQUIRED"),
              },
              control,
              errors,
            },
            layout: {
              colProps: { xs: 6, md: 6, lg: 4 },
            },
          })),
        }}
      />
      <ModalComponent modalProps={{ size: "lg" }} />
    </>
  );
};
